/* La lecture à voix haute, vue du parent.
 *
 * LE PARENT ÉTAIT AUTORISÉ ET N'AVAIT AUCUNE PORTE. `estResponsableDe` est
 * vérifié depuis le début par les trois lectures de fluence — historique,
 * compétences, progression — mais rien, dans l'application, ne menait un
 * responsable jusqu'à la lecture de son enfant. Un droit qu'on ne peut pas
 * exercer n'en est pas un.
 *
 * Ce que le parent voit n'est PAS ce que voit l'enseignant. Pas de comparaison
 * à la classe, pas de liste d'élèves : sa mesure, sa position, et le mouvement
 * des deux dernières lectures. Le reste ne le regarde pas, et le lui montrer
 * transformerait un suivi en classement. */

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { avecContexteNav } from '../lib/mini-app-contexte';
import { enDecrochage, estExtrapolee, pastilleDe } from '../pages/mini-apps/fluence-reperes';

interface Point {
  evaluationId: string; completedAt: string; supportTitre: string;
  mclm: number; dureeSecondes: number;
  position: 'sous' | 'dans' | 'au-dessus' | null;
}

const dateFr = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '';

export default function StudentFluenceCard({ studentId }: { studentId: string }) {
  const [points, setPoints] = useState<Point[]>([]);

  useEffect(() => {
    void (api as never as { miniApps: { fluence: { progression: { eleve: { query: (i: unknown) => Promise<{ points: Point[] }> } } } } })
      .miniApps.fluence.progression.eleve.query({ studentId })
      .then((lu) => setPoints(lu.points))
      /* Section facultative : un enfant peut n'avoir jamais été évalué, et la
         page de ses devoirs ne doit pas s'en trouver abîmée. */
      .catch(() => setPoints([]));
  }, [studentId]);

  if (points.length === 0) return null;

  const dernier = points[0]!;
  const pastille = pastilleDe(dernier.position);
  const alerte = enDecrochage(points);

  return (
    <div className="mb-[18px]">
      <h2 className="text-[16px] font-extrabold m-0 mb-2">Lecture à voix haute</h2>

      <div className="rounded-card border border-border bg-card p-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[32px] font-extrabold tabular-nums leading-none text-foreground">{dernier.mclm}</span>
          <span className="text-[13px] text-muted-foreground">
            mots correctement lus par minute{estExtrapolee(dernier.dureeSecondes) ? ' · extrapolé' : ''}
          </span>
          <span className={`ml-auto shrink-0 text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full ${pastille.cls}`}>
            {pastille.label}
          </span>
        </div>

        <p className="m-0 mt-2 text-[13px] text-muted-foreground">
          « {dernier.supportTitre} », le {dateFr(dernier.completedAt)}
          {points.length > 1 ? ` · ${points.length} lectures enregistrées` : ''}
        </p>

        {/* L'alerte se dit au parent comme à l'enseignant, mais SANS injonction :
            il n'a pas la main sur la remédiation, et lui donner une consigne
            qu'il ne peut pas suivre ne ferait que l'inquiéter. */}
        {alerte ? (
          <p className="m-0 mt-3 rounded-sm border border-naia-orange bg-naia-orange-bg px-3 py-2 text-[13px] text-foreground">
            Les deux dernières lectures sont sous le repère de son niveau. Une lecture faible
            arrive à tout le monde ; deux d&apos;affilée méritent d&apos;en parler avec son
            enseignant.
          </p>
        ) : null}

        {/* Un chiffre isolé se sur-interprète. La courbe montre qu'il bouge, et
            c'est le seul contexte qui empêche de lire une mesure comme un verdict. */}
        <p className="m-0 mt-3 text-[13px]">
          <a
            href={avecContexteNav(`/mini-apps/fluence-eleve-detail?student=${studentId}`, {
              contexte: 'Devoirs',
              retourHref: `/tutor/enfant/${studentId}`,
              retourLabel: 'Devoirs',
            })}
            className="font-bold text-primary"
          >
            Voir l&apos;évolution
          </a>
        </p>

        <p className="m-0 mt-2 text-[12px] text-muted-foreground leading-snug">
          Ce chiffre mesure une lecture, un jour donné. Il bouge quand on lit à voix haute
          régulièrement.
        </p>
      </div>
    </div>
  );
}
